<template>
  <div class="country-vn" :class="$x.nsAddressForm">
    <VTextField
      v-model="address.address1"
      name="address1"
      maxlength="45"
      required
      validate-on-blur
      :label="t.address1Label"
      class="flex-column"
      :rules="address1Rules"
      @blur="onBlur"
      @input="addressUpdated"
    />
    <VTextField
      v-model="address.address2"
      name="address2"
      maxlength="35"
      validate-on-blur
      class="flex-column"
      :aria-label="t.address2Label"
      :rules="address2Rules"
      @blur="onBlur"
      @input="addressUpdated"
    >
      <template #label>
        {{ t.address2Label }}
        <span :class="$x.optional"> {{ t.optional }} </span>
      </template>
    </VTextField>
    <div :class="$x.dualColumn">
      <NsSelect
        ref="region"
        v-model="address.region"
        :items="regions"
        name="region"
        :class="$x.firstCol"
        class="flex-column"
        :rules="stateRules"
        :label="t.regionLabel"
        @blur="onBlur"
        @input="addressUpdated"
      />
      <NsSelect
        ref="district"
        v-model="address.district"
        :items="districts"
        :disabled="!districts.length"
        name="district"
        maxlength="25"
        required
        validate-on-blur
        :label="t.districtLabel"
        class="flex-column"
        :rules="requiredRule"
        @blur="onBlur"
        @input="addressUpdated"
      />
    </div>
    <NsSelect
      ref="city"
      v-model="address.city"
      :items="cities"
      :disabled="!cities.length"
      name="city"
      maxlength="25"
      required
      validate-on-blur
      :label="t.cityLabel"
      class="flex-column"
      :rules="requiredRule"
      @blur="onBlur"
      @input="addressUpdated"
    />
  </div>
</template>

<script>
import { RunConfigService } from '@nuskin/ns-util';
import { axios, axiosRetry } from '@nuskin/axios-util';
import { VTextField } from 'vuetify/lib';
import NsSelect from '../../NsSelect.vue';
// Do not delete these are used by the mixin
import ADDRESS_MIXIN from '../addressMixin.js';
import REGIONS from './regions.json';
import TRANSLATIONS from './translations.json';

const MAX_RETRIES = 3;

const myAxios = axios.create();
axiosRetry(myAxios, {
  retries: MAX_RETRIES,
  shouldResetTimeout: true,
  retryDelay: (retryCount) => {
    return retryCount * 200;
  },
});

export default {
  name: 'NsAddressVn',
  components: {
    VTextField,
    NsSelect,
  },
  mixins: [ADDRESS_MIXIN],
  data() {
    return {
      regions: [],
    };
  },
  computed: {
    districts() {
      const selectedRegion = this.address.region;
      if (!selectedRegion) {
        return [];
      }
      const foundRegion = this.regions.find((r) => r.value === selectedRegion);
      if (!foundRegion) {
        return [];
      }
      return foundRegion.districts;
    },
    cities() {
      const selectedDistrict = this.address.district;
      if (!selectedDistrict) {
        return [];
      }
      const foundDistrict = this.districts.find(
        (d) => d.text === selectedDistrict
      );
      if (!foundDistrict) {
        return [];
      }
      return foundDistrict.cities;
    },
  },
  watch: {
    'address.region'() {
      this.$set(this.address, 'district', null);
      this.addressUpdated();
    },
    'address.district'() {
      this.$set(this.address, 'city', null);
      this.addressUpdated();
    },
  },
  created() {
    this.getRegionData();
  },
  mounted() {
    this.address.country = 'VN';
    if (TRANSLATIONS[this.lang]) {
      this.t = TRANSLATIONS[this.lang];
    } else {
      // If the language isn't available then get the first language in translations file
      this.t = TRANSLATIONS[Object.keys(TRANSLATIONS)[0]];
    }
    this.$emit('new-address-in-form');
  },
  methods: {
    async getRegionData() {
      const runConfig = RunConfigService.getRunConfig();
      const config = {
        method: 'GET',
        url: `${
          runConfig.fbBaseUrl ? runConfig.baseUrl : ''
        }/static/address-form-assets/vn-regions.json`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      try {
        const response = await myAxios(config);
        this.regions = response.data;
      } catch (err) {
        /* istanbul ignore next */
        // eslint-disable-next-line no-console
        console.error(
          'Error: failed loading vn-regions.json. Using default data.',
          err
        );
        this.regions = REGIONS;
      }
    },
  },
};
</script>

<style lang="scss" module="$x">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

$color-eeyore: map.get(colors.$baseColors, eyore);

::v-deep *:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.nsAddressForm {
  .dualColumn {
    display: flex;
    align-content: space-between;
    .firstCol {
      margin-right: 20px;
    }
  }
}

.optional {
  color: $color-eeyore;
}
</style>
